let _ = t => t,
  _t;
import { useEffect, useMemo, useRef } from 'react';
import styledWithTheme from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { H3 } from 'foundations-components/transitional/components/H3';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Image } from 'foundations-components/transitional/components/Image';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Pictogram } from 'foundations-components/transitional/components/Pictogram';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { startConfetti } from 'academy-common-lib/utils/confetti';
import HomePageSection from '../../../components/Layouts/HomePageSection';
import { PageSectionHeading } from '../../../components/SectionHeading';
import UsageTracker from '../../../utils/UsageTracker';
import SessionDateDisplay from '../../liveEvents/components/SessionDateDisplay';
import { useFullLiveEvent, useRegisterForSession } from '../../liveEvents/liveEventQueries';
import { MOCK_LIVE_EVENT_ID, MOCK_LIVE_EVENTS_LIST } from '../../liveEvents/liveEventMockData';
import { getSessionEndTime, getSessionStartTime, getUpcomingSessions } from '../../liveEvents/sessionTimeUtils';
import { useOverviewNavigation } from '../../overview/overviewRouting';

// TODO: Replace with value from API when available
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TOTAL_WORKSHOPS = MOCK_LIVE_EVENTS_LIST.length;
const CONFETTI_SESSION_KEY = 'ao-next-step-pending-confetti';
const ThumbnailWrapper = styledWithTheme.div(_t || (_t = _`
  aspect-ratio: 11 / 6;
  inline-size: 100%;
`));
const NextStepSectionLayout = ({
  children,
  registeredEventCount
}) => /*#__PURE__*/_jsxs(HomePageSection, {
  children: [/*#__PURE__*/_jsx(PageSectionHeading, {
    subtitle: /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "learningCenter.academyOnboarding.nextStepSubtitle",
      options: {
        registered: registeredEventCount,
        total: TOTAL_WORKSHOPS
      }
    }),
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.academyOnboarding.nextStepTitle"
    })
  }), children]
});
const NextStepSection = () => {
  var _MOCK_LIVE_EVENTS_LIS;
  const {
    isMobile,
    isTablet
  } = useMediaQuery();
  const {
    sessions,
    assignments,
    loading,
    error
  } = useFullLiveEvent(MOCK_LIVE_EVENT_ID);
  const {
    goTo
  } = useOverviewNavigation();
  const {
    registerForSession,
    registrationLoading
  } = useRegisterForSession({
    onError: () => {
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.liveEventOverviewModal.registrationError"
        })
      });
    }
  });
  const {
    registeredEventCount
  } = useMemo(() => {
    const regSessionIds = new Set(assignments.filter(a => a.status === 'registered').map(a => a.sessionId));
    const hasRegisteredSession = sessions.some(s => regSessionIds.has(s.id));
    return {
      registeredSessionIds: regSessionIds,
      registeredEventCount: hasRegisteredSession ? 1 : 0
    };
  }, [assignments, sessions]);
  const isAllRegistered = registeredEventCount >= TOTAL_WORKSHOPS;
  const nextEvent = (_MOCK_LIVE_EVENTS_LIS = MOCK_LIVE_EVENTS_LIST[registeredEventCount]) !== null && _MOCK_LIVE_EVENTS_LIS !== void 0 ? _MOCK_LIVE_EVENTS_LIS : null;
  const nextAvailableSession = useMemo(() => {
    var _getUpcomingSessions$;
    if (!nextEvent) return null;
    return (_getUpcomingSessions$ = getUpcomingSessions(sessions)[0]) !== null && _getUpcomingSessions$ !== void 0 ? _getUpcomingSessions$ : null;
  }, [sessions, nextEvent]);
  const confettiCleanupRef = useRef(undefined);
  const hasTriggeredConfettiRef = useRef(false);
  useEffect(() => {
    if (!hasTriggeredConfettiRef.current && isAllRegistered && sessionStorage.getItem(CONFETTI_SESSION_KEY)) {
      var _confettiCleanupRef$c;
      hasTriggeredConfettiRef.current = true;
      sessionStorage.removeItem(CONFETTI_SESSION_KEY);
      (_confettiCleanupRef$c = confettiCleanupRef.current) === null || _confettiCleanupRef$c === void 0 || _confettiCleanupRef$c.call(confettiCleanupRef);
      confettiCleanupRef.current = startConfetti();
    }
  }, [isAllRegistered]);
  useEffect(() => {
    return () => {
      var _confettiCleanupRef$c2;
      (_confettiCleanupRef$c2 = confettiCleanupRef.current) === null || _confettiCleanupRef$c2 === void 0 || _confettiCleanupRef$c2.call(confettiCleanupRef);
      confettiCleanupRef.current = undefined;
    };
  }, []);

  // TODO: Add loading and Error state
  if (loading || error) {
    return null;
  }
  if (isAllRegistered) {
    return /*#__PURE__*/_jsx(NextStepSectionLayout, {
      registeredEventCount: registeredEventCount,
      children: /*#__PURE__*/_jsxs(View, {
        marginBlock: "1000",
        textAlign: "center",
        children: [/*#__PURE__*/_jsx(Pictogram, {
          inlineSize: "100%",
          marginBlockEnd: "600",
          name: "CelebrationSuccess"
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.academyOnboarding.allWorkshopsRegistered"
          })
        })]
      })
    });
  }
  if (!nextEvent) {
    return /*#__PURE__*/_jsx(NextStepSectionLayout, {
      registeredEventCount: registeredEventCount,
      children: /*#__PURE__*/_jsxs(View, {
        marginBlock: "1000",
        textAlign: "center",
        children: [/*#__PURE__*/_jsx(Pictogram, {
          inlineSize: "100%",
          marginBlockEnd: "600",
          name: "EmptyStateSearch"
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.academyOnboarding.noNextStepsFound"
          })
        })]
      })
    });
  }
  const handleRegister = () => {
    if (!nextAvailableSession) {
      return;
    }
    if (registeredEventCount === TOTAL_WORKSHOPS - 1) {
      sessionStorage.setItem(CONFETTI_SESSION_KEY, '1');
    }
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'onboarding-next-step-register-click',
      contentType: 'LIVE_EVENT',
      contentId: nextEvent.id,
      sessionId: nextAvailableSession.id
    });
    void registerForSession(nextAvailableSession.id);
  };
  const handleShowOtherTimes = () => {
    UsageTracker.trackStandalone('Learning Center Interaction', {
      action: 'onboarding-next-step-show-other-times-click',
      contentType: 'LIVE_EVENT',
      contentId: nextEvent.id
    });
    goTo({
      id: nextEvent.id,
      type: 'LIVE_EVENT'
    });
  };
  return /*#__PURE__*/_jsx(NextStepSectionLayout, {
    registeredEventCount: registeredEventCount,
    children: /*#__PURE__*/_jsx(CardWrapper, {
      borderRadius: "300",
      marginBlockEnd: "0",
      padding: isMobile ? '600' : '1000',
      paddingBlock: isMobile ? '500' : '1000',
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        flexDirection: isMobile ? 'column' : 'row',
        gap: "900",
        children: [isMobile && nextEvent.previewThumbnailRectangleUrl && /*#__PURE__*/_jsx(View, {
          borderRadius: "300",
          overflow: "hidden",
          children: /*#__PURE__*/_jsx(Image, {
            alt: nextEvent.title,
            blockSize: "100%",
            inlineSize: "100%",
            objectFit: "cover",
            src: nextEvent.previewThumbnailRectangleUrl
          })
        }), /*#__PURE__*/_jsxs(Flex, {
          alignItems: "start",
          blockSize: "100%",
          flex: isTablet ? '1 1 0' : '2 1 0',
          flexDirection: "column",
          justifyContent: "center",
          children: [nextAvailableSession && /*#__PURE__*/_jsx(Text, {
            marginBlockEnd: "200",
            typography: "heading-50",
            children: /*#__PURE__*/_jsx(SessionDateDisplay, {
              endTime: getSessionEndTime(nextAvailableSession),
              startTime: getSessionStartTime(nextAvailableSession)
            })
          }), /*#__PURE__*/_jsx(H3, {
            marginBlockEnd: "200",
            typography: "heading-500",
            children: nextEvent.title
          }), /*#__PURE__*/_jsx(Text, {
            marginBlockEnd: "600",
            typography: "body-100",
            children: nextEvent.description
          }), /*#__PURE__*/_jsxs(Flex, {
            alignItems: isMobile ? 'center' : 'start',
            flexDirection: isMobile ? 'column' : 'row',
            gap: "300",
            inlineSize: isMobile ? '100%' : undefined,
            children: [/*#__PURE__*/_jsx(LoadingButton, {
              loading: registrationLoading,
              onClick: handleRegister,
              size: "medium",
              use: "primary",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.academyOnboarding.registerForWorkshop"
              })
            }), /*#__PURE__*/_jsx(Button, {
              onClick: handleShowOtherTimes,
              size: "medium",
              use: "transparent",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.academyOnboarding.showOtherTimes"
              })
            })]
          })]
        }), !isMobile && nextEvent.previewThumbnailRectangleUrl && /*#__PURE__*/_jsx(View, {
          alignSelf: "center",
          borderRadius: "300",
          flex: "1",
          overflow: "hidden",
          children: /*#__PURE__*/_jsx(ThumbnailWrapper, {
            children: /*#__PURE__*/_jsx(Image, {
              alt: nextEvent.title,
              blockSize: "100%",
              inlineSize: "100%",
              objectFit: "cover",
              src: nextEvent.previewThumbnailRectangleUrl
            })
          })
        })]
      })
    })
  });
};
export default NextStepSection;